import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as FlightSearchService from '_services/flight-search-service';

/**
 * Search for flights
 * @param {Object} params - Search parameters
 * @param {string} params.iata - Airport IATA code (required)
 * @param {string} params.date - Date in YYYY-MM-DD format (required)
 * @param {string} params.searchTerm - Flight number search term (required)
 * @param {string} params.type - 'departure' | 'arrival' | 'all' (optional, defaults to 'all')
 */
export const searchFlightsDropdown = createAsyncThunk(
    'flightSearchDropdown/searchFlights',
    async (params) => {
        const results = await FlightSearchService.searchFlights(params);
        
        // Filter by type if specified
        if (params.type === 'departure') {
            return (results || []).filter(
                flight => flight.departureAirport === params.iata || flight.type === 'departure'
            );
        } else if (params.type === 'arrival') {
            return (results || []).filter(
                flight => flight.arrivalAirport === params.iata || flight.type === 'arrival'
            );
        }
        
        return results || [];
    }
);

const initialState = {
    searchResults: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    selectedFlight: null,
};

const flightSearchDropdownSlice = createSlice({
    name: 'flightSearchDropdown',
    initialState,
    reducers: {
        // Reset the entire state
        resetFlightDropdown: (state) => {
            state.searchResults = [];
            state.status = 'idle';
            state.error = null;
            state.selectedFlight = null;
        },
        
        // Clear search results only
        clearFlightDropdownSearch: (state) => {
            state.searchResults = [];
            state.status = 'idle';
            state.error = null;
        },
        
        // Set selected flight
        setDropdownSelectedFlight: (state, action) => {
            state.selectedFlight = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(searchFlightsDropdown.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(searchFlightsDropdown.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // Sort by flight time
                state.searchResults = (action.payload || []).sort((a, b) => {
                    const timeA = new Date(a.operationInfo?.time || a.flightTime);
                    const timeB = new Date(b.operationInfo?.time || b.flightTime);
                    return timeA - timeB;
                });
            })
            .addCase(searchFlightsDropdown.rejected, (state, action) => {
                state.status = 'failed';
                state.searchResults = [];
                state.error = action.error.message;
            });
    },
});

// Export actions
export const { 
    resetFlightDropdown, 
    clearFlightDropdownSearch, 
    setDropdownSelectedFlight,
} = flightSearchDropdownSlice.actions;

// Export reducer
export default flightSearchDropdownSlice.reducer;

// Selectors
export const selectFlightDropdownResults = (state) => state.flightSearchDropdown.searchResults;
export const selectFlightDropdownStatus = (state) => state.flightSearchDropdown.status;
export const selectFlightDropdownError = (state) => state.flightSearchDropdown.error;
export const selectDropdownSelectedFlight = (state) => state.flightSearchDropdown.selectedFlight;

// Derived selectors
export const selectIsFlightDropdownLoading = (state) => state.flightSearchDropdown.status === 'loading';
export const selectHasFlightDropdownResults = (state) => 
    state.flightSearchDropdown.status === 'succeeded' && 
    state.flightSearchDropdown.searchResults.length > 0;
